"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreamEvmNftTransfer = void 0;
var common_core_1 = require("@moralisweb3/common-core");
var common_evm_utils_1 = require("@moralisweb3/common-evm-utils");
var StreamTriggerOutput_1 = require("../StreamTriggerOutput");
/**
 * The StreamEvmNftTransfer class is a representation of a nft transfer (EREC721 or ERC1155) that is returned by the Moralis Stream API
 *
 * @category DataType
 */
var StreamEvmNftTransfer = /** @class */ (function () {
    function StreamEvmNftTransfer(data, core) {
        this._data = StreamEvmNftTransfer.parse(data, core);
    }
    /**
     * Create a new instance of StreamEvmNftTransferish
     *
     * @param data - the StreamEvmNftTransferishish type
     * @param core - the Core instance
     * @example
     * ```ts
     * const transfer = StreamEvmTransactionish.create(data);
     * ```
     * @returns an instance of StreamEvmNftTransfer
     */
    StreamEvmNftTransfer.create = function (data, core) {
        if (data instanceof StreamEvmNftTransfer) {
            return data;
        }
        var finalCore = core !== null && core !== void 0 ? core : common_core_1.CoreProvider.getDefault();
        return new StreamEvmNftTransfer(data, finalCore);
    };
    StreamEvmNftTransfer.parse = function (data, core) {
        return __assign(__assign({}, data), { chain: common_evm_utils_1.EvmChain.create(data.chain, core), to: common_evm_utils_1.EvmAddress.create(data.to, core), contract: common_evm_utils_1.EvmAddress.create(data.contract, core), from: common_evm_utils_1.EvmAddress.create(data.from, core), logIndex: +data.logIndex, operator: (0, common_core_1.maybe)(data.operator, function (operator) { return common_evm_utils_1.EvmAddress.create(operator, core); }), tokenId: data.tokenId, transactionHash: data.transactionHash, amount: +data.amount, tokenName: data.tokenName, triggers: (0, common_core_1.maybe)(data.triggers, function (triggers) {
                return triggers.map(function (trigger) { return StreamTriggerOutput_1.StreamTriggerOutput.create(trigger, core); });
            }) });
    };
    /**
     * Compares two StreamEvmNftTransfer data. It checks a deep equality check of both values.
     * @param valueA - the first StreamEvmNftTransferish data to compare
     * @param valueB - the second StreamEvmNftTransferish data to compare
     * @returns true if the values are equal, false otherwise
     * @example
     * ```ts
     *  StreamEvmNftTransfer.equals(valueA, valueB);
     * ```
     */
    // eslint-disable-next-line complexity
    StreamEvmNftTransfer.equals = function (valueA, valueB) {
        var _a, _b;
        var transferA = StreamEvmNftTransfer.create(valueA);
        var transferB = StreamEvmNftTransfer.create(valueB);
        if (!transferA.chain.equals(transferB.chain)) {
            return false;
        }
        if (transferA.transactionHash !== transferB.transactionHash) {
            return false;
        }
        if (transferA.logIndex !== transferB.logIndex) {
            return false;
        }
        if (!transferA.contract.equals(transferB.contract)) {
            return false;
        }
        if (transferA.tokenId !== transferB.tokenId) {
            return false;
        }
        if (((_a = transferA.triggers) === null || _a === void 0 ? void 0 : _a.length) !== ((_b = transferB.triggers) === null || _b === void 0 ? void 0 : _b.length) ||
            !StreamTriggerOutput_1.StreamTriggerOutput.arrayEquals(transferA.triggers || [], transferB.triggers || [])) {
            return false;
        }
        return true;
    };
    /**
     * Compares an StreamEvmNftTransferish data to this StreamEvmNftTransfer instance.
     * @param value - the value to compare
     * @returns true if the value is equal to the current instance, false otherwise
     * @example
     * ```ts
     * transfer.equals(value);
     * ```
     */
    StreamEvmNftTransfer.prototype.equals = function (value) {
        return StreamEvmNftTransfer.equals(this, value);
    };
    /**
     * Converts the StreamEvmNftTransfer instance to a JSON object.
     * @returns JSON object of the StreamEvmNftTransfer instance
     * @example `transfer.toJSON()`
     */
    StreamEvmNftTransfer.prototype.toJSON = function () {
        var _a, _b;
        var data = this._data;
        return __assign(__assign({}, data), { chain: data.chain.format(), from: data.from.format(), to: data.to.format(), contract: data.contract.format(), operator: (_a = data.operator) === null || _a === void 0 ? void 0 : _a.format(), triggers: (_b = data.triggers) === null || _b === void 0 ? void 0 : _b.map(function (trigger) { return trigger.format(); }) });
    };
    /**
     * Converts the StreamEvmNftTransfer instance to a JSON object.
     * @returns JSON object of the StreamEvmNftTransfer instance
     * @example `transfer.toJSON()`
     */
    StreamEvmNftTransfer.prototype.format = function () {
        return this.toJSON();
    };
    Object.defineProperty(StreamEvmNftTransfer.prototype, "chain", {
        get: function () {
            return this._data.chain;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "transactionHash", {
        get: function () {
            return this._data.transactionHash;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "from", {
        get: function () {
            return this._data.from;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "to", {
        get: function () {
            return this._data.to;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "contract", {
        get: function () {
            return this._data.contract;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "logIndex", {
        get: function () {
            return this._data.logIndex;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "tokenId", {
        get: function () {
            return this._data.tokenId;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "amount", {
        get: function () {
            return this._data.amount;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "tokenContractType", {
        get: function () {
            return this._data.tokenContractType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "tokenName", {
        get: function () {
            return this._data.tokenName;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "tokenSymbol", {
        get: function () {
            return this._data.tokenSymbol;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "operator", {
        get: function () {
            return this._data.operator;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(StreamEvmNftTransfer.prototype, "triggers", {
        get: function () {
            return this._data.triggers;
        },
        enumerable: false,
        configurable: true
    });
    return StreamEvmNftTransfer;
}());
exports.StreamEvmNftTransfer = StreamEvmNftTransfer;
//# sourceMappingURL=StreamEvmNftTransfer.js.map